/** The card scanner's gateway client (STORY-089 M3): the reads from the teacher's photos go to
 * the session as card answers, through the gateway door, under a teacher-minted lease.
 *
 * - **The lease.** Minted once per sheet with the teacher's own token (`mintGatewayLease`,
 *   injected), and kept in this object only: never in storage, never logged. A 401 mints once
 *   more and retries; a second 401 stops the sheet. `api()` cannot carry it, because it sets
 *   the user's token on every call, so the two doors are called with `fetch` here.
 * - **A card joins** at its first read as `card:<course_id>:<n>` (`cardDeviceId`), channel
 *   `card`, with the label `setLabels` gave it (M4: its seat from the course's card rows, or
 *   its number, see `mapping.ts`). `<n>` is the printed number in ASCII digits without leading
 *   zeros, the key STORY-088 stores for a card, so the device id stays the same when M4 maps
 *   cards to seats.
 * - **No label, no join** (M4). Until `setLabels` has run, no card joins and nothing is posted:
 *   each read is refused as `unlabelled` (`labelled()` says whether labels are set). A card
 *   whose label is null (a named session of a course that maps cards to seats, and this card has
 *   none) is never joined. It may be in the session already, under the label it joined with
 *   before its seat row went (an earlier sheet, a page reloaded): so its answer is posted as it
 *   stands, as for a taken label below, and the door tells. Accepted, it keeps answering under the
 *   label it holds (it "keeps its seat until the lesson ends"); `not_joined`, it is `unmapped`:
 *   nothing counted, and its number is listed so the teacher can map it.
 * - **A label is decided once** (M4, second review): when the card's first join in this sheet
 *   starts, and kept while it joins and after. A later read never relabels or leaves out a card
 *   that has joined or is joining; only a card whose join was refused or never left is decided
 *   again.
 * - **Never two respondents with one label** (M4, second review). The server writes a label at a
 *   device's first join only, and does not keep labels unique, so a card's first join never
 *   takes a label someone in the session already has: one in the room's labels (`setRoomLabels`,
 *   from the teacher's live frame, so it holds across reopened sheets) or one this sheet's joins
 *   wrote or are writing. Such a card may be in the session already under that label (it joined
 *   from an earlier sheet), so it is not joined: its answer is posted as it stands, and the door
 *   tells. Accepted, it is in the session; `not_joined`, it is not, and it is left out (`taken`),
 *   with nothing counted, and listed by number.
 *   A join the server answers `rejoined` wrote no label (the device kept its first one), so its
 *   claim is dropped. A claim is dropped only on a definite refusal (a 4xx), never on a 5xx or a
 *   timeout, after which the row may have been written.
 *   The course is in the id because the session service finds a device's earlier person across
 *   the whole host. The `token` and `guest_token` a join returns are not read.
 * - **An answer** is posted only when a card's read differs from what this sheet last posted
 *   for it in this round: the option key at the read's position (the edge printed A answers
 *   the first option), `attempt` from the live frame, `occurred_at` the capture time on the
 *   server's clock, and a `seq` of whole server seconds since 2026-01-01, above the sheet's last
 *   for that card and reused on a retry: analytics stores `seq` as a Postgres integer, so it
 *   stays below 2^31. A read whose photo is more than 20 s old when it would be posted is not
 *   posted: the door refuses a stamp past its skew, and a stale answer would outrank a fresh
 *   one in the engine's latest-answer rule; the age is checked again once a request holds its
 *   slot. At most four requests are in flight.
 * - **Every request has a timeout** (`REQUEST_TIMEOUT_MS`, the mint too, raced against its
 *   signal so a token refresh inside it cannot hang the sheet): a hung request counts as a
 *   network failure, and a network failure or a 502-504 is retried once, with the same `seq`, so
 *   the store's idempotency key keeps a retry from counting twice. A second failure, a 502-504
 *   again, or a resend the age guard stops after the first try left, is a `network` refusal: the
 *   sheet asks for another photo.
 * - **A 2xx means it counted.** A post answered 2xx whose body cannot be read (it timed out
 *   mid-body) counts as acknowledged, and stays unsettled, so a later equal read is sent again. A
 *   refusal whose `counted_answer` is the key it posted (a resend that lands after the close,
 *   when its first send counted) is counted too.
 * - **What counts** is kept apart from what was last read: a card's `counted` is the answer
 *   the server holds (its own post's ack, or a refusal's `counted_answer`), so a later read that
 *   is beyond, too old or refused does not take the card out of the letter counts.
 * - **`dispose()`** when the live view closes (the gateway outlives the scan sheet, so closing
 *   the sheet loses nothing queued): requests already sent finish, queued ones never start, and
 *   nothing mints again, so a later view's lease is not revoked by this one.
 * - **Refusals** become sentences by their `code`, never the server's `detail` text. */
import { serviceUrl } from "../api/base";
import { channelSupports } from "../live/channels";

/** 2026-01-01T00:00:00Z in Unix seconds: the zero of a card answer's `seq`. */
export const SEQ_EPOCH_S = 1767225600;
/** The largest `seq` the door takes (analytics' integer column). */
export const MAX_SEQ = 2 ** 31 - 1;
/** A read is not posted when its photo is older than this when the post would leave. */
export const MAX_AGE_MS = 20_000;
/** A photo's own time counts when it is this close before the input changed; a photo older
 * than this is not read at all. */
export const CAPTURE_WINDOW_MS = 60_000;
export const MAX_IN_FLIGHT = 4;
/** No request, and no mint, waits longer than this. */
export const REQUEST_TIMEOUT_MS = 8_000;

/** A card's device id: `card:<course_id>:<n>`, `<n>` the printed number without leading
 * zeros. M4's roster test calls this too. */
export const cardDeviceId = (courseId: string, n: number): string => `card:${courseId}:${String(n)}`;

/** When the photo was taken, on this device's clock: the file's own time when it lies within
 * the minute before the input changed (a camera photo). A file whose own time is older than
 * that (a gallery or disk photo) is null: it is not read, because stamping it with the change
 * time would post an old photo as a fresh answer. The change time stands in only where the
 * file carries no time (missing, 0) or one in the future (a clock the page cannot trust). */
export function captureTime(lastModified: number | undefined, changedAt: number): number | null {
  if (lastModified === undefined || !Number.isFinite(lastModified) || lastModified <= 0 || lastModified > changedAt) return changedAt;
  return changedAt - lastModified <= CAPTURE_WINDOW_MS ? lastModified : null;
}

/** Whole seconds since 2026-01-01 at a server-clock instant. */
export const seqAt = (serverMs: number): number => Math.floor(serverMs / 1000) - SEQ_EPOCH_S;

/** Why a card's answer did not count, as the sheet says it. */
export type Refusal =
  | "locked"
  | "seatRequired"
  | "clock"
  | "notJoined"
  | "badChannel"
  | "closed"
  | "late"
  | "noItem"
  | "unknownRespondent"
  | "outsideLease"
  | "network"
  /** The course's card rows were not read, so no card may join. */
  | "unlabelled"
  | "other"
  /** The sheet stopped before this card's turn; the stop's own sentence says why. */
  | "stopped";
/** Why the whole sheet stopped. */
export type Stop = "lease" | "mint" | "ended" | "noSession";

/** `unmapped`: the course maps cards to seats and this card has none; it was not joined and
 * nothing was posted for it. `taken`: the label it would join with is someone else's in this
 * session; it was not joined and nothing counted. */
export type CardStatus = "sending" | "counted" | "refused" | "tooOld" | "beyond" | "unmapped" | "taken";
/** A card's latest read (`answer`, `status`) and, apart from it, the answer the server counts
 * for the card in this round (`counted`, a position; absent when none is known). */
export type CardState = { answer: number; status: CardStatus; refusal?: Refusal; counted?: number };

export type RoundItem = {
  item_id: string;
  attempt: number;
  snapshot: { type: string; options?: { key: string; image?: string | null }[] };
};

export type GatewayView = {
  /** `item_id:attempt` of the round the cards below belong to. */
  round: string | null;
  /** The latest read of each card in this round, by printed number. */
  cards: ReadonlyMap<number, CardState>;
  stopped: Stop | null;
  /** The cards whose join this sheet saw succeed, in any round. */
  joined: ReadonlySet<number>;
};

type FetchFn = (input: string, init: RequestInit) => Promise<Response>;

export type GatewayOptions = {
  sessionId: string;
  courseId: string;
  joinCode: string;
  /** Mint a lease with the teacher's own token: `mintGatewayLease` in `teacher/api.ts`. */
  mint: (signal: AbortSignal) => Promise<{ token: string }>;
  fetch?: FetchFn;
  /** This device's clock, in ms. */
  now?: () => number;
  onChange?: () => void;
  /** Tests shorten it; the sheet uses `REQUEST_TIMEOUT_MS`. */
  timeoutMs?: number;
};

const JOIN_REFUSALS: Record<string, Refusal> = {
  session_locked: "locked",
  seat_required: "seatRequired",
  bad_channel: "badChannel",
  device_outside_lease: "outsideLease",
  device_reserved: "outsideLease",
};
const ANSWER_REFUSALS: Record<string, Refusal> = {
  bad_occurred_at: "clock",
  not_joined: "notJoined",
  bad_channel: "badChannel",
  device_outside_lease: "outsideLease",
  device_reserved: "outsideLease",
};
/** The engine's refusals arrive as 200 `response_rejected`; its `detail` is known text. */
const ENGINE_REFUSALS: Record<string, Refusal> = {
  "item is closed": "closed",
  "after the deadline": "late",
  "no such open item": "noItem",
  "unknown respondent": "unknownRespondent",
};
const STOPS: Record<string, Stop> = { session_ended: "ended", no_such_code: "noSession", no_such_session: "noSession" };

class Stopped extends Error {}
/** The request did not reach the server, or its answer did not come back in time. */
class NetworkFailure extends Error {}
/** The photo grew too old while its post waited for a slot. */
class TooOld extends Error {}

/** Rejects when `signal` aborts, whatever `work` does with it. */
function bounded<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new NetworkFailure());
  return Promise.race([work, new Promise<never>((_, fail) => signal.addEventListener("abort", () => fail(new NetworkFailure()), { once: true }))]);
}

const isNetwork = (e: unknown): boolean => {
  const name = (e as { name?: unknown } | null)?.name;
  return e instanceof TypeError || e instanceof NetworkFailure || name === "AbortError" || name === "TimeoutError";
};

function timeout(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}

type Reply = {
  status: number;
  body: { code?: string; kind?: string; rejoined?: unknown; data?: { code?: string; detail?: string; counted_answer?: unknown } } | null;
};

export class CardGateway {
  private readonly opts: GatewayOptions;
  private readonly fetchFn: FetchFn;
  private readonly now: () => number;
  /** The lease, in memory only. */
  private lease: Promise<string> | null = null;
  private readonly joins = new Map<number, Promise<void>>();
  private readonly lastSeq = new Map<number, number>();
  private readonly tickets = new Map<number, number>();
  private ticket = 0;
  private active = 0;
  private readonly queue: { go: () => void; fail: (e: Error) => void }[] = [];
  private round: string | null = null;
  /** The round's option keys, to read a refusal's `counted_answer`. */
  private keys: string[] = [];
  private cards = new Map<number, CardState>();
  /** The seq behind each card's `counted`, so an older post's late ack cannot replace it. */
  private countedSeq = new Map<number, number>();
  /** Each card's posts that were sent and have no known outcome: no reply, a reply that could
   * not be read, a 502-504 twice, a resend the age guard stopped. Any of them may have landed,
   * so while one is left the card's `counted` may be stale, and a read equal to it is sent. */
  private unsettled = new Map<number, Set<number>>();
  private stopped: Stop | null = null;
  private disposed = false;
  /** The label each card joins with, null for a card left out; until `setLabels`, none joins. */
  private labelOf: ((card: number) => string | null) | null = null;
  private readonly joinedCards = new Set<number>();
  /** Each card's label, decided when its first join in this sheet started. */
  private readonly fixed = new Map<number, string>();
  /** The labels this sheet's joins wrote or are writing, by card; a refused join drops its own. */
  private readonly claims = new Map<number, string>();
  /** The labels the session's respondents hold, from the teacher's live frame. */
  private roomLabels = new Set<string>();

  constructor(opts: GatewayOptions) {
    this.opts = opts;
    this.fetchFn = opts.fetch ?? ((input, init) => fetch(input, init));
    this.now = opts.now ?? (() => Date.now());
  }

  /** The sheet closed: requests already sent finish, queued ones never start, nothing mints
   * again and nothing is reported. */
  dispose() {
    this.disposed = true;
    this.release();
  }

  /** The label a card joins with (M4: from the course's card rows), or null to leave it out. A
   * card keeps the label of its first join for the rest of the session, so the scan sheet sets
   * this before any join, and again before each photo's posts with the rows read for it. A card
   * that has joined or is joining keeps the label it was given. */
  setLabels(labelOf: (card: number) => string | null) {
    this.labelOf = labelOf;
  }

  /** The labels the session's respondents hold now (the staff frame's `labels`); an empty one,
   * an anonymous session's, is no label. */
  setRoomLabels(labels: Iterable<string>) {
    this.roomLabels = new Set([...labels].filter((l) => l !== ""));
  }

  /** Whether `label` is someone else's: a respondent's in the room, or another card's join here. */
  private taken(card: number, label: string): boolean {
    if (this.roomLabels.has(label)) return true;
    for (const [c, l] of this.claims) if (c !== card && l === label) return true;
    return false;
  }

  /** Whether labels are set: until they are, no card joins. */
  labelled(): boolean {
    return this.labelOf !== null;
  }

  view(): GatewayView {
    return { round: this.round, cards: new Map(this.cards), stopped: this.stopped, joined: new Set(this.joinedCards) };
  }

  /** Post a photo's reads for the open round. Returns once every post has settled. */
  async submit(
    reads: readonly { card: number; answer: number }[],
    ctx: { item: RoundItem; captureAt: number; skewMs: number },
  ): Promise<void> {
    if (this.stopped || this.disposed) return;
    const round = `${ctx.item.item_id}:${ctx.item.attempt}`;
    if (round !== this.round) {
      this.round = round;
      this.cards = new Map();
      this.countedSeq = new Map();
      this.unsettled = new Map();
    }
    this.keys = (ctx.item.snapshot.options ?? []).map((o) => o.key);
    if (!channelSupports("card", ctx.item.snapshot)) {
      this.changed();
      return;
    }
    const options = ctx.item.snapshot.options ?? [];
    const labelOf = this.labelOf;
    const work: Promise<void>[] = [];
    for (const { card, answer } of reads) {
      const prev = this.cards.get(card);
      const counted = prev?.counted;
      // a card that has joined or is joining keeps its label; any other is decided now
      const label = this.fixed.get(card) ?? (labelOf ? labelOf(card) : undefined);
      if (label === undefined) {
        // fail closed: nothing joins and nothing is posted without the course's rows
        this.tickets.set(card, ++this.ticket);
        this.cards.set(card, { answer, status: "refused", refusal: "unlabelled", counted });
        continue;
      }
      if (label === null && answer >= options.length) {
        // an unmapped card's read the room has no bar for: nothing to probe with
        this.tickets.set(card, ++this.ticket);
        this.cards.set(card, { answer, status: "unmapped", counted });
        continue;
      }
      if (answer >= options.length) {
        // C or D on a two-option question: the room sees no such bar, so it is not posted. It
        // takes a ticket all the same, so an earlier post's result cannot overwrite this read.
        this.tickets.set(card, ++this.ticket);
        this.cards.set(card, { answer, status: "beyond", counted });
        continue;
      }
      if (prev && prev.answer === answer && prev.status === "sending") continue;
      if (prev && prev.status !== "sending" && counted === answer && !this.unsettled.get(card)?.size) {
        // the server holds this answer, nothing else is on its way and no post's fate is
        // unknown: nothing to send
        this.tickets.set(card, ++this.ticket);
        this.cards.set(card, { answer, status: "counted", counted });
        continue;
      }
      const ticket = ++this.ticket;
      this.tickets.set(card, ticket);
      this.cards.set(card, { answer, status: "sending", counted });
      if (label !== null) this.fixed.set(card, label);
      work.push(this.post(card, answer, options[answer]!.key, round, ticket, ctx, label));
    }
    this.changed();
    await Promise.all(work);
  }

  private changed() {
    if (!this.disposed) this.opts.onChange?.();
  }

  /** This post is about to leave: until the server answers it, its outcome is unknown. */
  private sending(card: number, seq: number) {
    let seqs = this.unsettled.get(card);
    if (!seqs) this.unsettled.set(card, (seqs = new Set()));
    seqs.add(seq);
  }

  /** The server answered this post, counted or refused: its outcome is known. */
  private answered(card: number, seq: number) {
    this.unsettled.get(card)?.delete(seq);
  }

  /** A post's result. `posted` is the answer and seq that post carried: an ack makes it the
   * card's counted answer whether or not a newer read has taken the card's place since (the
   * server counts it all the same), unless a later seq is already counted. */
  private settle(
    card: number,
    round: string,
    ticket: number,
    state: Omit<CardState, "answer" | "counted">,
    posted?: { answer: number; seq: number | null; server?: unknown },
  ) {
    // a card that neither joined nor is joining has its label decided again at its next read
    if (!this.joinedCards.has(card) && !this.joins.has(card)) this.fixed.delete(card);
    if (round !== this.round) return; // a new round: this result is old news
    const prev = this.cards.get(card);
    if (!prev) return;
    const latest = this.tickets.get(card) === ticket;
    let counted = prev.counted;
    if (posted && state.status === "counted" && posted.seq !== null && posted.seq >= (this.countedSeq.get(card) ?? -1)) {
      counted = posted.answer;
      this.countedSeq.set(card, posted.seq);
    } else if (posted && latest && posted.server !== undefined) {
      // a refusal names the answer the engine still holds for this card in this round
      const at = typeof posted.server === "string" ? this.keys.indexOf(posted.server) : -1;
      counted = at >= 0 ? at : undefined;
    }
    this.cards.set(card, latest ? { answer: prev.answer, ...state, counted } : { ...prev, counted });
    this.changed();
  }

  private stop(why: Stop) {
    if (!this.stopped) this.stopped = why;
    this.release();
    this.changed();
  }

  /** Queued requests never start once the sheet stopped or closed. */
  private release() {
    for (const w of this.queue.splice(0)) w.fail(new Stopped());
  }

  /** At most MAX_IN_FLIGHT at once. A finished request hands its slot straight to the next
   * waiter, so no newcomer can take it in between. `ready` runs once the slot is held. */
  private async slot<T>(fn: () => Promise<T>, ready: () => void): Promise<T> {
    if (this.active >= MAX_IN_FLIGHT) await new Promise<void>((go, fail) => this.queue.push({ go, fail }));
    else this.active++;
    try {
      ready();
      return await fn();
    } finally {
      const next = this.queue.shift();
      if (next) next.go();
      else this.active--;
    }
  }

  private gone() {
    if (this.stopped || this.disposed) throw new Stopped();
  }

  private token(): Promise<string> {
    if (!this.lease) {
      const signal = timeout(this.opts.timeoutMs ?? REQUEST_TIMEOUT_MS);
      const lease = bounded(this.opts.mint(signal), signal).then(
        (r) => r.token,
        (e: unknown) => {
          if (isNetwork(e)) {
            // the mint did not reach the server: the next photo mints again
            if (this.lease === lease) this.lease = null;
            throw new NetworkFailure();
          }
          this.stop(STOPS[(e as { code?: string })?.code ?? ""] ?? "mint");
          throw new Stopped();
        },
      );
      this.lease = lease;
    }
    return this.lease;
  }

  /** One request under a lease: a 401 mints once more and retries; a second 401 stops. A
   * network failure or a 502-504 is sent once more, the same body, and a second one is a
   * `NetworkFailure`. `guard` runs once the request holds its slot, told whether this is the
   * resend, and throws to send nothing. */
  private async call(path: string, body: unknown, guard?: (resend: boolean) => void): Promise<Reply> {
    let stale: Promise<string> | null = null;
    let resent = false;
    for (;;) {
      this.gone();
      // after a 401: mint once more, unless another request already has (compared by the
      // promise, synchronously, so two refusals of one lease share one new mint)
      if (stale && this.lease === stale) this.lease = null;
      const current = this.token();
      const lease = await current;
      let reply: Reply;
      try {
        reply = await this.slot(
          async () => {
            const res = await this.fetchFn(serviceUrl(path), {
              method: "POST",
              headers: { Authorization: `Bearer ${lease}`, "Content-Type": "application/json" },
              body: JSON.stringify(body),
              signal: timeout(this.opts.timeoutMs ?? REQUEST_TIMEOUT_MS),
            });
            let parsed: Reply["body"] = null;
            try {
              parsed = (await res.json()) as Reply["body"];
            } catch {
              parsed = null;
            }
            // a stop is decided while the slot is still held, so no queued request starts
            const stop = STOPS[parsed?.code ?? ""];
            if (stop) this.stop(stop);
            return { status: res.status, body: parsed };
          },
          () => {
            this.gone();
            guard?.(resent);
          },
        );
      } catch (e) {
        if (!isNetwork(e)) throw e;
        if (resent) throw new NetworkFailure();
        resent = true;
        continue;
      }
      if (reply.status === 401) {
        if (stale !== null) {
          this.stop("lease");
          throw new Stopped();
        }
        stale = current;
        continue;
      }
      if (reply.status >= 502 && reply.status <= 504) {
        if (resent) throw new NetworkFailure();
        resent = true;
        continue;
      }
      return reply;
    }
  }

  /** Join a card under `label`, the label `submit` fixed for it: at its first join, and again
   * under the same label after a host lost it. */
  private join(card: number, label: string): Promise<void> {
    let pending = this.joins.get(card);
    if (!pending) {
      // the last guard: never a join without a label
      if (!this.labelOf || !label) return Promise.reject(new Refused("unlabelled"));
      this.claims.set(card, label);
      pending = (async () => {
        const reply = await this.call("/session/v1/sessions/join", {
          code: this.opts.joinCode,
          device_id: cardDeviceId(this.opts.courseId, card),
          label,
          channel: "card",
        });
        // the respondent and guest tokens in a 200 are not this scanner's to keep
        if (reply.status >= 200 && reply.status < 300) {
          this.joinedCards.add(card);
          // the device was in the session already: the server kept its first label, not this one
          if (reply.body?.rejoined === true && this.claims.get(card) === label) this.claims.delete(card);
          return;
        }
        const code = reply.body?.code ?? "";
        if (STOPS[code]) {
          this.stop(STOPS[code]);
          throw new Stopped();
        }
        // a definite refusal wrote nothing, so the label is free again; a 5xx may have written it
        if (reply.status >= 400 && reply.status < 500 && this.claims.get(card) === label) this.claims.delete(card);
        throw new Refused(JOIN_REFUSALS[code] ?? "other");
      })();
      this.joins.set(card, pending);
      // a refused join may succeed later (the teacher unlocks): forget it
      pending.catch(() => this.joins.delete(card));
    }
    return pending;
  }

  private nextSeq(card: number, skewMs: number): number {
    const seq = Math.min(MAX_SEQ, Math.max(seqAt(this.now() + skewMs), (this.lastSeq.get(card) ?? -1) + 1));
    this.lastSeq.set(card, seq);
    return seq;
  }

  private async post(
    card: number,
    answer: number,
    key: string,
    round: string,
    ticket: number,
    ctx: { item: RoundItem; captureAt: number; skewMs: number },
    label: string | null,
  ): Promise<void> {
    let seq: number | null = null;
    try {
      if (this.now() - ctx.captureAt > MAX_AGE_MS) {
        this.settle(card, round, ticket, { status: "tooOld" });
        return;
      }
      // no label (unmapped), or one someone in the session has, is never a first join's: the
      // card's answer goes as it stands, and the door tells whether it is in the session already
      const probe = !this.joinedCards.has(card) && !this.joins.has(card) && (label === null || this.taken(card, label));
      if (!probe && !this.joinedCards.has(card) && label !== null) await this.join(card, label);
      let rejoined = false;
      for (;;) {
        if (round !== this.round || this.tickets.get(card) !== ticket) return;
        if (this.now() - ctx.captureAt > MAX_AGE_MS) {
          this.settle(card, round, ticket, { status: "tooOld" });
          return;
        }
        seq ??= this.nextSeq(card, ctx.skewMs);
        const sent = seq;
        const reply = await this.call(
          `/session/v1/sessions/${this.opts.sessionId}/channel-responses`,
          {
            channel: "card",
            device_id: cardDeviceId(this.opts.courseId, card),
            seq: sent,
            item_id: ctx.item.item_id,
            attempt: ctx.item.attempt,
            answer: key,
            occurred_at: new Date(ctx.captureAt + ctx.skewMs).toISOString(),
          },
          (resend) => {
            // the wait for a slot may have made the photo too old for the door's bound; on the
            // resend the first try already left, so its fate is unknown, not "not sent"
            if (this.now() - ctx.captureAt > MAX_AGE_MS) throw resend ? new NetworkFailure() : new TooOld();
            this.sending(card, sent);
          },
        );
        const body = reply.body;
        const code = body?.code ?? "";
        // the door found the card's respondent: it is in the session, under its own label
        if (probe && (reply.status === 200 || code === "bad_occurred_at")) this.joinedCards.add(card);
        if (reply.status === 200 && body?.kind === "response_ack") {
          this.answered(card, sent);
          this.settle(card, round, ticket, { status: "counted" }, { answer, seq });
          return;
        }
        if (reply.status >= 200 && reply.status < 300 && body === null) {
          // a 2xx whose body never arrived (it timed out mid-read): the answer counted. Left
          // unsettled, so a later read of the same letter is sent again rather than trusted.
          this.settle(card, round, ticket, { status: "counted" }, { answer, seq });
          return;
        }
        if (reply.status === 200 && body?.kind === "error") {
          this.answered(card, sent);
          const rejected = body.data?.code === "response_rejected";
          const refusal = rejected ? (ENGINE_REFUSALS[body.data?.detail ?? ""] ?? "other") : "other";
          const server = rejected && body.data && "counted_answer" in body.data ? (body.data.counted_answer ?? null) : undefined;
          if (server === key) {
            // the engine holds the answer this post carried: an earlier send of it counted (a
            // resend that landed after the close and its grace)
            this.settle(card, round, ticket, { status: "counted" }, { answer, seq });
            return;
          }
          this.settle(card, round, ticket, { status: "refused", refusal }, { answer, seq, server });
          return;
        }
        if (STOPS[code]) {
          this.stop(STOPS[code]);
          throw new Stopped();
        }
        if (code === "not_joined" && !rejoined) {
          // a label this sheet's own join wrote is the card's, whatever the frame still shows
          const own = label !== null && this.claims.get(card) === label;
          if (probe || label === null || (this.taken(card, label) && !own)) {
            // not in the session, and with no seat or a label that is someone else's: left out,
            // nothing counted
            this.answered(card, sent);
            this.settle(card, round, ticket, { status: label === null ? "unmapped" : "taken" });
            return;
          }
          // the host lost the card (a restart on a new database): join it again, once
          rejoined = true;
          this.joins.delete(card);
          this.joinedCards.delete(card);
          await this.join(card, label);
          continue;
        }
        // bad_occurred_at is dropped, never resent without a stamp: the sheet asks for a photo.
        // A door refusal it can name counted nothing; anything else (no body, a 5xx twice) may
        // have, and stays unsettled.
        if (ANSWER_REFUSALS[code]) this.answered(card, sent);
        this.settle(card, round, ticket, { status: "refused", refusal: ANSWER_REFUSALS[code] ?? "other" });
        return;
      }
    } catch (e) {
      if (e instanceof TooOld) {
        this.settle(card, round, ticket, { status: "tooOld" });
        return;
      }
      const refusal: Refusal = e instanceof Refused ? e.refusal : e instanceof Stopped ? "stopped" : "network";
      this.settle(card, round, ticket, { status: "refused", refusal });
    }
  }
}

class Refused extends Error {
  constructor(readonly refusal: Refusal) {
    super(refusal);
  }
}

/** What the sheet states for the round, from the gateway's view: counts, and the printed numbers
 * of the cards left out, never a letter beside a number. */
export type RoundSummary = {
  /** Per letter A to D (as many as the item has options, at most four): cards counted or on
   * their way. A card whose latest read was not sent (beyond, too old, refused) stays under
   * the letter the server still counts for it. */
  letters: number[];
  /** Cards read in this round, whatever became of them. */
  read: number;
  /** Reads naming a letter the question does not have; not posted. */
  beyond: number;
  /** Reads from a photo too old to post. */
  tooOld: number;
  refused: { refusal: Refusal; count: number }[];
  /** Posts not yet answered. */
  inFlight: number;
  /** The printed numbers of the cards left out because they have no seat, in order. */
  unmapped: number[];
  /** The printed numbers of the cards left out because their label is someone else's in this
   * session, in order. */
  taken: number[];
};

export function summarise(view: GatewayView, optionCount: number): RoundSummary {
  const letters = Array.from({ length: Math.min(4, Math.max(0, optionCount)) }, () => 0);
  const refused = new Map<Refusal, number>();
  let beyond = 0;
  let tooOld = 0;
  let inFlight = 0;
  const unmapped: number[] = [];
  const taken: number[] = [];
  const tally = (at: number | undefined) => {
    if (at !== undefined && at < letters.length) letters[at]! += 1;
  };
  for (const [n, c] of view.cards) {
    if (c.status === "unmapped") unmapped.push(n);
    if (c.status === "taken") taken.push(n);
    if (c.status === "sending" || c.status === "counted") {
      if (c.status === "sending") inFlight++;
      tally(c.answer);
      continue;
    }
    if (c.status === "unmapped" || c.status === "taken") continue;
    if (c.status === "beyond") beyond++;
    else if (c.status === "tooOld") tooOld++;
    else if (c.refusal && c.refusal !== "stopped") refused.set(c.refusal, (refused.get(c.refusal) ?? 0) + 1);
    tally(c.counted);
  }
  return {
    letters,
    read: view.cards.size,
    beyond,
    tooOld,
    refused: [...refused].map(([refusal, count]) => ({ refusal, count })),
    inFlight,
    unmapped: unmapped.sort((a, b) => a - b),
    taken: taken.sort((a, b) => a - b),
  };
}
